import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { useToast } from "../Toast";

import { userService } from "@/api/user";
import { useStore } from "@/store";

type TConsentType =
  | "marketing"
  | "data_sharing"
  | "analytics"
  | "profiling"
  | "third_party"
  | "realtime_chat"
  | "websocket_monitoring"
  | "ai_chat_processing"
  | "chat_history_retention"
  | "file_upload_processing";

type TConsentRecord = {
  consent_id: string;
  consent_type: TConsentType;
  consent_value: boolean;
  timestamp: string;
  policy_version: string;
};

const CONSENT_OPTIONS: { type: TConsentType; label: string; description: string }[] = [
  { type: "marketing", label: "Marketing", description: "Product updates and marketing communications." },
  { type: "analytics", label: "Analytics", description: "Help improve the product by sharing usage analytics." },
  { type: "data_sharing", label: "Data sharing", description: "Share data with trusted partners to improve services." },
  { type: "profiling", label: "Profiling", description: "Personalize your experience based on usage patterns." },
  { type: "third_party", label: "Third-party", description: "Allow third-party processing for certain features." },
  {
    type: "ai_chat_processing",
    label: "AI chat processing",
    description: "Process chat messages to generate AI responses.",
  },
  {
    type: "chat_history_retention",
    label: "Chat history retention",
    description: "Store chat history to improve continuity.",
  },
  {
    type: "file_upload_processing",
    label: "File upload processing",
    description: "Process uploaded files to generate insights.",
  },
  { type: "realtime_chat", label: "Realtime chat", description: "Enable realtime chat features." },
  {
    type: "websocket_monitoring",
    label: "Websocket monitoring",
    description: "Monitor websocket reliability and performance.",
  },
];

// Backend requires policy_version for consent writes.
const DEFAULT_POLICY_VERSION = "v1";

export const ConsentSettings = () => {
  const { toast } = useToast();
  const profile = useStore.use.profile();
  const userId = profile?.id ?? null;
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingType, setIsSavingType] = useState<TConsentType | null>(null);
  const [records, setRecords] = useState<TConsentRecord[]>([]);

  const map = useMemo(() => {
    const m = new Map<TConsentType, TConsentRecord>();
    records.forEach((r) => m.set(r.consent_type, r));
    return m;
  }, [records]);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await userService.getConsents();
    setIsLoading(false);

    if (result.error) {
      toast({ variant: "destructive", title: "Failed to load consents" });
      return;
    }

    const data = result.data?.data as unknown;
    if (!Array.isArray(data)) {
      setRecords([]);
      return;
    }

    setRecords(data as TConsentRecord[]);
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback(
    async (type: TConsentType, nextValue: boolean) => {
      if (!userId) {
        toast({ variant: "destructive", title: "Your profile is not loaded yet. Please refresh and try again." });
        return;
      }

      setIsSavingType(type);
      const result = await userService.recordConsent({
        user_id: userId,
        consent_type: type,
        consent_value: nextValue,
        policy_version: DEFAULT_POLICY_VERSION,
      });
      setIsSavingType(null);

      if (result.error) {
        toast({ variant: "destructive", title: "Failed to update consent" });
        return;
      }

      toast({ variant: "success", title: "Consent updated" });
      await load();
    },
    [load, toast, userId]
  );

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold">Privacy</h3>
        <p className="text-sm text-muted-foreground">Manage your consent preferences.</p>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => void load()}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Refresh"}
        </Button>
      </div>

      <div className="space-y-3">
        {CONSENT_OPTIONS.map((opt) => {
          const record = map.get(opt.type);
          const checked = record ? Boolean(record.consent_value) : false;
          const disabled = isSavingType !== null || isLoading;
          return (
            <div
              key={opt.type}
              className="rounded-lg border bg-background p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="text-sm font-semibold">{opt.label}</div>
                  <div className="text-sm text-muted-foreground">{opt.description}</div>
                  {record?.timestamp ? (
                    <div className="text-xs text-muted-foreground">Last updated: {record.timestamp}</div>
                  ) : null}
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(val) => void toggle(opt.type, Boolean(val))}
                  />
                  {isSavingType === opt.type ? <span className="text-xs text-muted-foreground">Saving…</span> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
